'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

// ── Scroll-triggered fade-in hook ──────────────────────────
function useFadeIn() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

// ── Fade wrapper ───────────────────────────────────────────
function FadeIn({ children, delay = 0, className = '', style = {} }: { children: React.ReactNode; delay?: number; className?: string; style?: React.CSSProperties }) {
  const { ref, visible } = useFadeIn();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity 0.75s cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 0.75s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── Nav ────────────────────────────────────────────────────
function LandingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToHowItWorks = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        transition: 'background 0.35s ease, box-shadow 0.35s ease',
        background: scrolled || mobileMenuOpen ? 'rgba(245,240,232,0.96)' : 'transparent',
        backdropFilter: scrolled || mobileMenuOpen ? 'blur(12px)' : 'none',
        boxShadow: scrolled ? '0 1px 0 rgba(26,26,26,0.08)' : 'none',
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Logo */}
        <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '8px',
            background: 'linear-gradient(135deg, #8B6914 0%, #b07d5a 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M3 4h12M3 8h8M3 12h10" stroke="#F5F0E8" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </div>
          <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, fontSize: '17px', color: '#1a1a1a', letterSpacing: '-0.01em' }}>
            Notes On This
          </span>
        </Link>

        {/* Desktop right nav */}
        <div className="desktop-nav-row1" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <a
            href="#how-it-works"
            onClick={scrollToHowItWorks}
            style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 400, color: '#5a4a3a', textDecoration: 'none', padding: '8px 14px', borderRadius: '6px', transition: 'color 0.2s' }}
          >
            See how it works
          </a>
          <Link
            href="/login"
            style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 400, color: '#1a1a1a', textDecoration: 'none', padding: '8px 14px', borderRadius: '6px', transition: 'background 0.2s' }}
          >
            Sign in
          </Link>
          <Link
            href="/signup"
            style={{
              fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 500,
              color: '#F5F0E8', textDecoration: 'none',
              padding: '9px 20px', borderRadius: '8px',
              background: 'linear-gradient(135deg, #8B6914 0%, #b07d5a 100%)',
              transition: 'opacity 0.2s',
            }}
          >
            Start free
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className="mobile-nav-toggle"
          onClick={() => setMobileMenuOpen((v) => !v)}
          style={{
            display: 'none',
            alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#1a1a1a', padding: '4px',
          }}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path d="M4 4L18 18M18 4L4 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path d="M3 6h16M3 11h16M3 16h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div style={{
          borderTop: '1px solid rgba(139,105,20,0.12)',
          padding: '16px 24px 24px',
          display: 'flex', flexDirection: 'column', gap: '4px',
          background: 'rgba(245,240,232,0.98)',
        }}>
          <a
            href="#how-it-works"
            onClick={scrollToHowItWorks}
            style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', fontWeight: 400, color: '#5a4a3a', textDecoration: 'none', padding: '12px 0', borderBottom: '1px solid rgba(139,105,20,0.08)' }}
          >
            See how it works
          </a>
          <Link
            href="/login"
            onClick={() => setMobileMenuOpen(false)}
            style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', fontWeight: 400, color: '#1a1a1a', textDecoration: 'none', padding: '12px 0', borderBottom: '1px solid rgba(139,105,20,0.08)' }}
          >
            Sign in
          </Link>
          <Link
            href="/signup"
            onClick={() => setMobileMenuOpen(false)}
            style={{
              fontFamily: 'Inter, sans-serif', fontSize: '15px', fontWeight: 500,
              color: '#F5F0E8', textDecoration: 'none',
              padding: '13px 20px', borderRadius: '10px', marginTop: '8px',
              background: 'linear-gradient(135deg, #8B6914 0%, #b07d5a 100%)',
              textAlign: 'center',
            }}
          >
            Start free
          </Link>
        </div>
      )}
    </nav>
  );
}

// ── Hero mock screenshot ───────────────────────────────────
function HeroMockup() {
  const cols = [
    { label: 'This Week', color: '#8B6914', tasks: ['Finalise Q3 OKR deck', 'Review brand voice doc', 'Send stockist follow-ups'] },
    { label: 'In Progress', color: '#b07d5a', tasks: ['Website copy refresh', 'Email sequence — onboarding', 'Ops metrics dashboard'] },
    { label: 'Done', color: '#6b8a5a', tasks: ['Weekly recap AI review', 'Team sync — Monday', 'Klaviyo segment audit'] },
  ];

  return (
    <div style={{
      borderRadius: '16px',
      overflow: 'hidden',
      border: '1px solid rgba(139,105,20,0.15)',
      boxShadow: '0 32px 80px rgba(26,26,26,0.12), 0 8px 24px rgba(139,105,20,0.08)',
      background: '#faf7f2',
    }}>
      {/* Window chrome */}
      <div style={{ background: '#f0ebe2', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid rgba(139,105,20,0.1)' }}>
        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#e8b4a0' }} />
        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#e8d4a0' }} />
        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#b8d4a0' }} />
        <span style={{ marginLeft: '12px', fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#8a7864', fontWeight: 400 }}>Action Board — Notes On This</span>
      </div>
      {/* Kanban — horizontal scroll on mobile */}
      <div className="action-board-columns" style={{ padding: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
        {cols.map((col) => (
          <div key={col.label} className="action-board-column" style={{ minWidth: '180px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: col.color }} />
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 500, color: '#5a4a3a', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{col.label}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {col.tasks.map((task) => (
                <div key={task} style={{
                  background: '#fff',
                  border: '1px solid rgba(139,105,20,0.1)',
                  borderRadius: '6px',
                  padding: '10px 12px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '12px',
                  color: '#2a1f14',
                  fontWeight: 300,
                  lineHeight: 1.4,
                }}>
                  {task}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────
export default function LandingPage() {
  const router = useRouter();
  const { currentUser, allOrganizations, isLoading } = useAuth();

  // Redirect authenticated users with an active org to /app
  useEffect(() => {
    if (isLoading) return;
    if (!currentUser) return;
    if (allOrganizations && allOrganizations.length > 0) {
      router.replace('/app');
    }
  }, [currentUser, allOrganizations, isLoading]);

  return (
    <div style={{ background: '#F5F0E8', minHeight: '100vh', overflowX: 'hidden' }}>
      <LandingNav />

      {/* ── 1. HERO ─────────────────────────────────────── */}
      <section style={{ paddingTop: '140px', paddingBottom: '100px', maxWidth: '1200px', margin: '0 auto', padding: '140px 24px 100px' }}>
        <FadeIn>
          <div style={{ textAlign: 'center', maxWidth: '760px', margin: '0 auto' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: 'rgba(139,105,20,0.08)', border: '1px solid rgba(139,105,20,0.2)',
              borderRadius: '100px', padding: '6px 16px', marginBottom: '32px',
            }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#8B6914' }} />
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#8B6914', fontWeight: 500, letterSpacing: '0.04em' }}>
                Built for founders who think deeply
              </span>
            </div>

            <h1 style={{
              fontFamily: 'Outfit, sans-serif',
              fontSize: 'clamp(40px, 6vw, 72px)',
              fontWeight: 300,
              color: '#1a1a1a',
              lineHeight: 1.08,
              letterSpacing: '-0.03em',
              marginBottom: '24px',
            }}>
              The operating system<br />
              <span style={{ color: '#8B6914' }}>for businesses that</span><br />
              think clearly.
            </h1>

            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '18px',
              fontWeight: 300,
              color: '#5a4a3a',
              lineHeight: 1.65,
              maxWidth: '560px',
              margin: '0 auto 40px',
            }}>
              Notes On This combines AI with proven psychology frameworks to help entrepreneurs and small teams turn scattered thinking into real momentum.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link
                href="/signup"
                style={{
                  fontFamily: 'Inter, sans-serif', fontSize: '15px', fontWeight: 500,
                  color: '#F5F0E8', textDecoration: 'none',
                  padding: '14px 28px', borderRadius: '10px',
                  background: 'linear-gradient(135deg, #8B6914 0%, #b07d5a 100%)',
                  boxShadow: '0 4px 16px rgba(139,105,20,0.25)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  display: 'inline-block',
                }}
              >
                Create your free workspace
              </Link>
              <Link
                href="/portal/demo"
                style={{
                  fontFamily: 'Inter, sans-serif', fontSize: '15px', fontWeight: 400,
                  color: '#1a1a1a', textDecoration: 'none',
                  padding: '14px 28px', borderRadius: '10px',
                  background: 'rgba(26,26,26,0.06)',
                  border: '1px solid rgba(26,26,26,0.12)',
                  transition: 'background 0.2s',
                  display: 'inline-block',
                }}
              >
                Explore a live demo
              </Link>
            </div>
          </div>
        </FadeIn>

        <FadeIn delay={200} className="">
          <div style={{ marginTop: '72px', maxWidth: '960px', margin: '72px auto 0' }}>
            <HeroMockup />
          </div>
        </FadeIn>
      </section>

      {/* ── 2. HOW IT WORKS ─────────────────────────────── */}
      <section id="how-it-works" style={{ padding: '100px 24px', background: '#fff' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: '64px' }}>
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 500, color: '#8B6914', letterSpacing: '0.12em', textTransform: 'uppercase', display: 'block', marginBottom: '16px' }}>
                How it works
              </span>
              <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 300, color: '#1a1a1a', letterSpacing: '-0.025em', lineHeight: 1.15 }}>
                From scattered to structured<br />in one conversation.
              </h2>
            </div>
          </FadeIn>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2px', background: 'rgba(139,105,20,0.08)', borderRadius: '16px', overflow: 'hidden' }}>
            {[
              {
                step: '01',
                title: 'Tell us about your business',
                body: 'A short AI-guided conversation sets up your entire workspace around how you actually work.',
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" fill="#8B6914" opacity="0.7"/>
                    <path d="M8 10h8M8 14h5" stroke="#8B6914" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                ),
              },
              {
                step: '02',
                title: 'Your portal is built for you',
                body: 'Your modules, your metrics, your rhythm — configured by AI for your business type.',
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="8" height="8" rx="2" stroke="#8B6914" strokeWidth="1.5"/>
                    <rect x="13" y="3" width="8" height="8" rx="2" stroke="#8B6914" strokeWidth="1.5"/>
                    <rect x="3" y="13" width="8" height="8" rx="2" stroke="#8B6914" strokeWidth="1.5"/>
                    <rect x="13" y="13" width="8" height="8" rx="2" stroke="#8B6914" strokeWidth="1.5" opacity="0.4"/>
                  </svg>
                ),
              },
              {
                step: '03',
                title: 'Move with clarity every day',
                body: 'Action boards, cadence reviews, goal tracking, and AI insights — all in one place.',
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12l5 5L20 7" stroke="#8B6914" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ),
              },
            ].map((item, i) => (
              <FadeIn key={item.step} delay={i * 120}>
                <div style={{ background: '#fff', padding: '48px 40px' }}>
                  <div style={{
                    width: '48px', height: '48px', borderRadius: '12px',
                    background: 'rgba(139,105,20,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: '24px',
                  }}>
                    {item.icon}
                  </div>
                  <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 500, color: '#8B6914', letterSpacing: '0.1em', marginBottom: '12px' }}>
                    STEP {item.step}
                  </div>
                  <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '22px', fontWeight: 400, color: '#1a1a1a', letterSpacing: '-0.02em', marginBottom: '12px', lineHeight: 1.25 }}>
                    {item.title}
                  </h3>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', fontWeight: 300, color: '#5a4a3a', lineHeight: 1.65 }}>
                    {item.body}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. FIVE CORE MODULES ────────────────────────── */}
      <section style={{ padding: '100px 24px', background: '#F5F0E8' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <FadeIn>
            <div style={{ marginBottom: '64px' }}>
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 500, color: '#8B6914', letterSpacing: '0.12em', textTransform: 'uppercase', display: 'block', marginBottom: '16px' }}>
                Five core modules
              </span>
              <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 300, color: '#1a1a1a', letterSpacing: '-0.025em', lineHeight: 1.15, maxWidth: '520px' }}>
                Everything your business needs.<br />Nothing it doesn't.
              </h2>
            </div>
          </FadeIn>

          {/* Bento grid — varied sizes */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
            {[
              {
                title: 'Action Board',
                body: 'A smart Kanban that auto-prioritises and surfaces what needs attention.',
                accent: '#8B6914',
                bg: 'rgba(139,105,20,0.06)',
                icon: '⬡',
              },
              {
                title: 'Cadence & Rhythm',
                body: 'Daily and weekly checklists that build habits high-performing teams run on.',
                accent: '#b07d5a',
                bg: 'rgba(176,125,90,0.06)',
                icon: '◎',
              },
              {
                title: 'Goals & OKRs',
                body: 'Track revenue and objectives with a visual that keeps the big picture in sight.',
                accent: '#6b8a5a',
                bg: 'rgba(107,138,90,0.06)',
                icon: '△',
              },
              {
                title: 'AI Review Layer',
                body: 'Weekly recaps, stale task alerts, SOP drafting, brand voice checking — all AI-powered.',
                accent: '#8B6914',
                bg: 'rgba(139,105,20,0.04)',
                icon: '✦',
              },
              {
                title: 'Business Metrics',
                body: 'Marketing, product, and operations dashboards built for your specific business type.',
                accent: '#b07d5a',
                bg: 'rgba(176,125,90,0.04)',
                icon: '◈',
              },
            ].map((card, i) => (
              <FadeIn key={card.title} delay={i * 80}>
                <div style={{
                  background: '#fff',
                  border: `1px solid rgba(139,105,20,0.1)`,
                  borderRadius: '14px',
                  padding: '36px 32px',
                  height: '100%',
                  minHeight: '160px',
                  position: 'relative',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    position: 'absolute', top: '20px', right: '24px',
                    fontSize: '28px', opacity: 0.12, color: card.accent,
                    fontFamily: 'monospace',
                  }}>
                    {card.icon}
                  </div>
                  <h3 style={{
                    fontFamily: 'Outfit, sans-serif', fontSize: '20px', fontWeight: 400,
                    color: '#1a1a1a', letterSpacing: '-0.02em', marginBottom: '10px',
                  }}>
                    {card.title}
                  </h3>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 300, color: '#5a4a3a', lineHeight: 1.6, maxWidth: '340px' }}>
                    {card.body}
                  </p>
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', background: `linear-gradient(90deg, ${card.accent}40, transparent)` }} />
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. WHO IT'S FOR ─────────────────────────────── */}
      <section style={{ padding: '100px 24px', background: '#fff' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: '64px' }}>
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 500, color: '#8B6914', letterSpacing: '0.12em', textTransform: 'uppercase', display: 'block', marginBottom: '16px' }}>
                Who it's for
              </span>
              <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 300, color: '#1a1a1a', letterSpacing: '-0.025em', lineHeight: 1.15 }}>
                Built for people who build things.
              </h2>
            </div>
          </FadeIn>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
            {[
              {
                persona: 'Founders & Entrepreneurs',
                tagline: 'Stop living in your inbox.',
                body: 'Run your business from one clear system — not a dozen disconnected tools.',
                number: '01',
              },
              {
                persona: 'Small Teams (2–15 people)',
                tagline: 'Get everyone aligned.',
                body: 'Shared priorities, shared rhythm — without endless meetings or status updates.',
                number: '02',
              },
              {
                persona: 'Agencies & Studios',
                tagline: 'Scale without chaos.',
                body: 'Manage multiple client projects with a system that grows as you do.',
                number: '03',
              },
            ].map((p, i) => (
              <FadeIn key={p.persona} delay={i * 100}>
                <div style={{
                  padding: '40px 36px',
                  border: '1px solid rgba(139,105,20,0.12)',
                  borderRadius: '14px',
                  background: '#faf7f2',
                  position: 'relative',
                }}>
                  <div style={{
                    fontFamily: 'Outfit, sans-serif', fontSize: '56px', fontWeight: 200,
                    color: 'rgba(139,105,20,0.12)', lineHeight: 1,
                    position: 'absolute', top: '24px', right: '28px',
                  }}>
                    {p.number}
                  </div>
                  <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '22px', fontWeight: 400, color: '#1a1a1a', letterSpacing: '-0.02em', marginBottom: '8px', lineHeight: 1.2 }}>
                    {p.persona}
                  </h3>
                  <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '17px', fontWeight: 400, color: '#8B6914', marginBottom: '14px', lineHeight: 1.3 }}>
                    {p.tagline}
                  </p>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 300, color: '#5a4a3a', lineHeight: 1.65 }}>
                    {p.body}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. TESTIMONIAL ──────────────────────────────── */}
      <section style={{ padding: '100px 24px', background: '#F5F0E8' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <FadeIn>
            <div style={{
              width: '40px', height: '2px',
              background: 'linear-gradient(90deg, #8B6914, #b07d5a)',
              margin: '0 auto 40px',
              borderRadius: '2px',
            }} />
            <blockquote style={{
              fontFamily: 'Outfit, sans-serif',
              fontSize: 'clamp(22px, 3.5vw, 34px)',
              fontWeight: 300,
              color: '#1a1a1a',
              lineHeight: 1.35,
              letterSpacing: '-0.02em',
              marginBottom: '32px',
            }}>
              "We went from scattered notes and Slack chaos to a system that actually runs our business."
            </blockquote>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #8B6914, #b07d5a)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'Outfit, sans-serif', fontSize: '14px', fontWeight: 500, color: '#F5F0E8',
              }}>
                S
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 500, color: '#1a1a1a' }}>Sarah K.</div>
                <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 300, color: '#8a7864' }}>Founder</div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── 6. FINAL CTA ────────────────────────────────── */}
      <section style={{ padding: '120px 24px', background: '#1a1a1a' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
          <FadeIn>
            <h2 style={{
              fontFamily: 'Outfit, sans-serif',
              fontSize: 'clamp(32px, 5vw, 56px)',
              fontWeight: 300,
              color: '#F5F0E8',
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
              marginBottom: '40px',
            }}>
              Your clearest thinking<br />starts here.
            </h2>
            <Link
              href="/signup"
              style={{
                fontFamily: 'Inter, sans-serif', fontSize: '16px', fontWeight: 500,
                color: '#1a1a1a', textDecoration: 'none',
                padding: '16px 36px', borderRadius: '10px',
                background: '#F5F0E8',
                display: 'inline-block',
                transition: 'opacity 0.2s',
                boxShadow: '0 4px 24px rgba(245,240,232,0.15)',
              }}
            >
              Create your free workspace
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* ── 7. FOOTER ───────────────────────────────────── */}
      <footer style={{ background: '#1a1a1a', borderTop: '1px solid rgba(245,240,232,0.08)', padding: '48px 24px 40px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '32px', marginBottom: '40px' }}>
            {/* Brand */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '7px',
                  background: 'linear-gradient(135deg, #8B6914 0%, #b07d5a 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                    <path d="M3 4h12M3 8h8M3 12h10" stroke="#F5F0E8" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </div>
                <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, fontSize: '16px', color: '#F5F0E8' }}>Notes On This</span>
              </div>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 300, color: '#8a7864' }}>
                Think clearly. Build better.
              </p>
            </div>

            {/* Links */}
            <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
              {[
                { label: 'Product', href: '#' },
                { label: 'Pricing', href: '/pricing' },
                { label: 'About', href: '#' },
                { label: 'Privacy', href: '#' },
                { label: 'Terms', href: '#' },
              ].map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 300, color: '#8a7864', textDecoration: 'none', transition: 'color 0.2s' }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div style={{ borderTop: '1px solid rgba(245,240,232,0.06)', paddingTop: '24px' }}>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 300, color: '#5a4a3a' }}>
              © 2026 Notes On This. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}